import type { ComponentType, ReactNode } from 'react'
import { useMemo, useRef, useState } from 'react'
import { useSettingsEmbedded } from '@/components/shared/settings/settings-embed'
import { TableScrollRootContext } from '@/components/shared/table-scroll-root'
import { Skeleton } from '@/components/ui/skeleton'
import { RecordTabGapContext } from '@/context/RecordTabGapContext'
import {
  ContainerWidth,
  PageLayoutMode,
  useLayout,
} from '@/contexts/LayoutContext'
import { useNavigationItems } from '@/hooks/useNavigationItems'
import { useScreenWidth } from '@/hooks/useScreenWidth'
import { useScrollCollapse } from '@/hooks/useScrollCollapse'
import { FLOW_VIEWPORT } from '@/lib/surfaces'
import { cn } from '@/lib/utils'
import { useLocation } from 'react-router-dom'

import { ModuleTypeEnum } from '@scrambl/shared'

import { type DetailHeaderSlots } from './DetailHeader'
import { Page } from './Page'
import { PageHeader } from './PageHeader'
import { TabItem } from './PageTabs'

/**
 * The horizontal page gutter, published as `--page-px` alongside the matching
 * `px-*` utilities. Split out from `getContainerWidthClasses` because the flow
 * toolbar slot — which sits outside the padded content column — has to declare
 * the SAME variable so `.flow-toolbar` can put the gutter back as padding and
 * line its controls up with the table underneath.
 */
const PAGE_PX_VARS =
  '[--page-px:1rem] sm:[--page-px:1.5rem] md:[--page-px:2rem] lg:[--page-px:3rem]'
const PAGE_PX_VARS_NO_PADDING =
  '[--page-px:1rem] sm:[--page-px:1.5rem] md:[--page-px:0px]'

interface PageContainerProps {
  children: ReactNode
  header?: {
    title: string | ReactNode
    titleSuffix?: string
    description?: ReactNode
    greeting?: boolean
    actions?: ReactNode
    /** The page's overflow ("⋯") menu; see PageHeader. */
    menu?: ReactNode
    children?: ReactNode
    tabs?: TabItem[]
    className?: string
    condensed?: boolean
    fixed?: boolean
    /** Drops the divider under the header (open surfaces like Today). */
    flat?: boolean
    icon?: ComponentType<{ className?: string; weight?: string }> | null
    moduleType?: ModuleTypeEnum
    entityData?: {
      id?: string
      name?: string
      logoUrl?: string
    }
    onEntityClick?: () => void
    onLogoChange?: (file: File | null) => Promise<void>
    backLink?: {
      label: string
      href: string
    }
    /** 20 px title (default) or the 32 px display title for hub landing pages. */
    titleSize?: 'default' | 'display'
    /** Record page header slots; see DetailHeader.tsx. */
    detail?: DetailHeaderSlots
    /** Decorative layer rendered absolutely behind the header block */
    background?: ReactNode
    /** Full-width hero content rendered inside the header block, between the title row and the tabs */
    hero?: ReactNode
    /** A row of its own between the title row and the tabs, inside the header gutter */
    strip?: ReactNode
  }
  className?: string
  isLoading?: boolean
  fullWidth?: boolean
  fullHeight?: boolean
  noHeaderSpacing?: boolean
  noPadding?: boolean
  /**
   * @deprecated No effect. Full-height columns never carry bottom padding and
   * growing pages keep their scroll clearance, so there is nothing to turn off.
   */
  noBottomPadding?: boolean
  /** Results own their internal spacing; remove the surrounding page gutters. */
  edgeToEdge?: boolean
  containerWidth?: ContainerWidth
  enableScroll?: boolean
  /**
   * 'fill' (default) keeps the nested-scroller layout: the header is pinned
   * and each panel below owns a scroller. 'flow' turns the page into a single
   * document scroller — the header stays pinned above it and tables stop
   * creating viewports of their own (iOS-style page scrolling).
   */
  layout?: PageLayoutMode
  /**
   * A record page (client, project, vendor, …). Its frame owns the one gap
   * between the tab bar and the content, `RECORD_TAB_GAP`, on every tab and in
   * every scroll mode (and under the header alone when a record shows a
   * single tab, so no tab bar), and publishes `RecordTabGapContext` so the chrome that
   * leads a tab (the record canvas, Settings-style side menus) starts flush
   * instead of adding a gap of its own.
   */
  isRecord?: boolean
}

/**
 * The gap between a record's tab bar and its content. Padding, not margin: a
 * full-height column fills the scroller exactly, and border-box padding keeps
 * that height exact where a margin would push it past the frame's bottom edge.
 */
const RECORD_TAB_GAP = 'pt-4'

export function PageContainer({
  children,
  header,
  className,
  isLoading,
  fullWidth,
  fullHeight,
  noHeaderSpacing,
  noPadding,
  edgeToEdge = false,
  containerWidth,
  enableScroll = true,
  layout,
  isRecord = false,
}: PageContainerProps) {
  // Get layout options from context
  const { layoutOptions } = useLayout()
  const { isUltrawide, isLargeDesktop, category } = useScreenWidth()
  const isEmbedded = useSettingsEmbedded()

  // Use location to determine if we're on a settings page
  const location = useLocation()
  const { mainNavItems, adminNavItems } = useNavigationItems()
  // The Settings hub (`/:org/settings/...`, `/admin/settings/...`) reads in a
  // centered column. A module's own Settings tab (`/team/settings`,
  // `/people/:id/pay/settings`) is just another tab of that module and keeps
  // the module's width, so switching to it never moves the page.
  const isSettingsPage = /^\/(?:admin|[^/]+)\/settings(?:\/|$)/.test(
    location.pathname,
  )

  const rootRef = useRef<HTMLDivElement>(null)
  const scrollContainerRef = useRef<HTMLDivElement>(null)

  // Check if this is a discussions page or tab - either standalone or nested in another module
  const isDiscussionsPage = location.pathname.includes('/discussions')
  const isThreadsPage = location.pathname.includes('/threads')
  const currentPathSegment = location.pathname.split('/').pop()
  const isDiscussionsTab = currentPathSegment === 'discussions'
  const isAnyDiscussionsPage =
    isDiscussionsPage || isDiscussionsTab || isThreadsPage

  // Check if this is a workflows page (canvas-style layout)
  const isWorkflowsPage = location.pathname.includes('/workflows')

  // For discussions pages/tabs, threads, and workflows, they handle their own scrolling
  const isCanvasStyleLayout = isAnyDiscussionsPage || isWorkflowsPage
  // Disable scroll for canvas-style layouts - they handle their own scrolling internally
  const effectiveEnableScroll = isCanvasStyleLayout ? false : enableScroll

  // Page-scroller mode. Canvas layouts run their own scrollers and pages with
  // scrolling disabled have no page scroller to hand out, so neither can flow.
  const isFlow =
    (layout ?? layoutOptions.layout ?? 'fill') === 'flow' &&
    !isCanvasStyleLayout &&
    effectiveEnableScroll

  // Collapse the header to one line once the reader scrolls into the
  // content, whichever element owns the scroll. Canvas layouts (discussions,
  // workflows) are excluded — a chat thread opens scrolled to the bottom.
  const isScrolled = useScrollCollapse(rootRef, {
    enabled: !!header && !isCanvasStyleLayout,
    resetKey: location.pathname,
  })

  // Merge layout options from context with props (context takes precedence)
  const effectiveFullWidth = fullWidth ?? layoutOptions.fullWidth ?? false
  const effectiveFullHeight = fullHeight ?? layoutOptions.fullHeight ?? false
  const effectiveNoHeaderSpacing =
    noHeaderSpacing ?? layoutOptions.noHeaderSpacing ?? false
  const effectiveNoPadding = noPadding ?? layoutOptions.noPadding ?? false
  const effectiveContainerWidth =
    containerWidth ?? layoutOptions.containerWidth ?? 'default'

  // Helper function to get container width classes
  const getContainerWidthClasses = (
    width: ContainerWidth,
    noPadding: boolean,
    enableScroll: boolean,
  ) => {
    // Always include minimum padding on smaller screens for usability
    // On md+ screens, respect noPadding for full-width layouts
    // --page-px mirrors the active horizontal padding so full-bleed children
    // can cancel it exactly with mx-[calc(var(--page-px,0px)*-1)] regardless
    // of which padding mode is active (see forms builder edit page).
    const paddingClasses = noPadding
      ? `px-4 sm:px-6 md:px-0 ${PAGE_PX_VARS_NO_PADDING}`
      : `px-4 sm:px-6 md:px-8 lg:px-12 ${PAGE_PX_VARS}`

    switch (width) {
      case 'full':
        // Only add overflow-x-auto if scroll is disabled to avoid conflicts
        return `${paddingClasses} w-full text-left ${!enableScroll ? 'overflow-x-auto' : ''}`
      case 'adaptive':
        // Adaptive based on actual window size (not screen size)
        if (isUltrawide) {
          // Very wide window (2200px+)
          return `max-w-[1800px] ${paddingClasses}`
        } else if (isLargeDesktop) {
          // Large window (1600px+)
          return `max-w-[1600px] ${paddingClasses}`
        } else if (category === 'medium') {
          // Medium window (1200-1599px)
          return `max-w-[1400px] ${paddingClasses}`
        } else {
          // Small window (<1200px)
          return `max-w-[1200px] ${paddingClasses}`
        }
      case 'wide':
        // Static wide width
        return `max-w-[1600px] ${paddingClasses}`
      case 'default':
      default:
        return `max-w-[1400px] ${paddingClasses}`
    }
  }

  // For discussions pages/tabs and workflows, force fullHeight and noPadding
  // Note: isCanvasStyleLayout and effectiveEnableScroll are defined earlier for use in the scroll effect
  // A page that turns the page scroller off hands scrolling to its own
  // content (a split view, a canvas, a nested list). That content needs a
  // definite height to scroll at all, so the column is always the full-height
  // flex chain, whether or not the page remembered to ask for `fullHeight`.
  // Full-height columns are gapless either way: pinned to the frame, the
  // bottom padding a growing page keeps for its last rows would only be a
  // dead band under content that scrolls on its own.
  const ownsScrolling = !effectiveEnableScroll
  const shouldUseFullHeight =
    effectiveFullHeight || isCanvasStyleLayout || ownsScrolling
  // For canvas-style layouts, force noPadding to true
  const shouldNoPadding = effectiveNoPadding || isCanvasStyleLayout
  // Keep the historical inner padding for ordinary fill pages. Results own
  // their spacing in both page-scrolling and split-view layouts; adding Page's
  // gutter here would inset their tables even when --page-px is zero.
  const shouldUsePadding =
    effectiveNoPadding && !isCanvasStyleLayout && !isFlow && !edgeToEdge

  // Determine if we have tabs to adjust spacing accordingly
  // PageHeader only renders a tab bar for two or more tabs, so a lone tab
  // counts as no tabs for spacing.
  const hasTabs = header?.tabs && header.tabs.length > 1

  // For settings pages, always use default width
  // For fullWidth or canvas-style layouts (discussions, workflows), use 'full' containerWidth
  // Otherwise use the specified containerWidth
  // Flow pages are always full width: a max-w + mx-auto content column would
  // leave the sticky first column pinned to the page scroller's left edge
  // rather than the table's.
  // Tabbed pages also run full width: their header always spans the frame,
  // and a centered content column would drift away from it as the window
  // widens (a module's Settings tab sat 12px+ right of its title). Content
  // that reads best narrow caps its own width, left-aligned.
  const finalContainerWidth = edgeToEdge
    ? 'full'
    : isSettingsPage
      ? 'default'
      : effectiveFullWidth || isCanvasStyleLayout || isFlow || hasTabs
        ? 'full'
        : effectiveContainerWidth

  const containerClasses = edgeToEdge
    ? 'w-full px-0 text-left [--page-px:0px]'
    : getContainerWidthClasses(
        finalContainerWidth,
        shouldNoPadding,
        effectiveEnableScroll,
      )

  const resolvedHeaderIcon = (() => {
    if (!header) return undefined
    if (header.greeting) return header.icon ?? undefined
    if (header.icon === null) return undefined
    if (header.icon) return header.icon

    const navItems = [...mainNavItems, ...adminNavItems]
    const segments = location.pathname.split('/').filter(Boolean)
    const candidates = [location.pathname]

    // Most app routes are org-scoped (/:orgSlug/...), so check both full and de-scoped paths.
    if (segments.length > 1) {
      candidates.push(`/${segments.slice(1).join('/')}`)
    }

    const linkable = navItems.filter(item => item.href !== '/today')
    const inSection = (href: string) =>
      candidates.some(
        candidate => candidate === href || candidate.startsWith(`${href}/`),
      )
    // A nav item can deep-link inside its module (Notes opens /notes/mine), so
    // the module's other pages (/notes, /notes/recordings) match on its root
    // segment; otherwise their titles lost the icon and shifted sideways.
    // Only a root one nav item owns: /admin/* is shared by several items and
    // must not borrow a sibling's icon.
    const moduleRoot = (href: string) => `/${href.split('/')[1] ?? ''}`
    const rootOwners = new Map<string, number>()
    for (const item of linkable) {
      const root = moduleRoot(item.href)
      rootOwners.set(root, (rootOwners.get(root) ?? 0) + 1)
    }
    const matchedItem =
      linkable
        .filter(item => inSection(item.href))
        .sort((a, b) => b.href.length - a.href.length)[0] ??
      linkable.find(item => {
        const root = moduleRoot(item.href)
        return root !== '/' && rootOwners.get(root) === 1 && inSection(root)
      })

    if (matchedItem?.icon) {
      return matchedItem.icon as ComponentType<{
        className?: string
        weight?: string
      }>
    }

    return undefined
  })()

  // Published to descendant tables so they stop building scrollers of their
  // own. A getter, not the node: the ref is empty on first render and a state
  // write per mount would re-render every table on the page. The toolbar slot
  // is the exception — FlowStickyBar cannot portal into it until it exists, so
  // that one has to be state.
  const [toolbarSlot, setToolbarSlot] = useState<HTMLElement | null>(null)
  const [overlaySlot, setOverlaySlot] = useState<HTMLElement | null>(null)
  const tableScrollRoot = useMemo(
    () => ({
      getElement: () => scrollContainerRef.current,
      toolbarSlot,
      overlaySlot,
    }),
    [toolbarSlot, overlaySlot],
  )

  const rootHeightClasses = isCanvasStyleLayout
    ? '' // Canvas layouts (threads, discussions, workflows) rely on h-full from parent chain
    : effectiveEnableScroll
      ? 'h-full'
      : 'min-h-full'

  const headerNode = header ? (
    <PageHeader
      title={header.title}
      titleSuffix={header.titleSuffix}
      description={header.description}
      greeting={header.greeting}
      actions={header.actions}
      menu={header.menu}
      tabs={header.tabs}
      condensed={header.condensed}
      collapsed={isScrolled}
      fixed={header.fixed}
      flat={header.flat}
      icon={resolvedHeaderIcon}
      moduleType={header.moduleType}
      entityData={header.entityData}
      onEntityClick={header.onEntityClick}
      onLogoChange={header.onLogoChange}
      backLink={header.backLink}
      titleSize={header.titleSize}
      detail={header.detail}
      background={header.background}
      hero={header.hero}
      strip={header.strip}
      fullWidth={effectiveFullWidth}
      noPadding={effectiveNoPadding}
      // A tabbed page's title and tabs stay put whichever tab is open: each
      // tab may pick its own content width (edge-to-edge tables, centered
      // forms), but the header always spans the frame.
      containerWidth={hasTabs ? 'full' : finalContainerWidth}
      isLoading={isLoading}
      className={cn(
        effectiveNoHeaderSpacing ? 'mb-0' : '',
        // Pinned in BOTH modes: the title and tabs are page chrome, not part of
        // the scrolling document. In flow mode the filter bar joins it in the
        // pinned block (see the toolbar slot below), so only the rows and the
        // pagination move.
        'flex-shrink-0',
        header.className,
      )}
    >
      {header.children}
    </PageHeader>
  ) : null

  // Hosted inside the Settings shell: the shell is the page, so this is a
  // plain content block. PageHeader renders its own compact form there.
  if (isEmbedded) {
    return (
      <div className={cn('min-w-0', className)}>
        {headerNode}
        {children}
      </div>
    )
  }

  return (
    <div
      ref={rootRef}
      className={cn(
        'bg-canvas flex h-full min-h-0 flex-col',
        rootHeightClasses,
      )}
    >
      {/* The header sits OUTSIDE the scroll container in both modes so the
        title and tabs never scroll away. Scrolling collapses the header to one
        row (hysteresis in the listener above keeps it from flickering), which
        only grows the scroll viewport — sticky chrome inside it stays put. */}
      {headerNode}

      {/* Filter-bar slot. Empty (and therefore zero height) unless the page
        renders a `FlowStickyBar`, which portals into it. Living here — between
        the pinned header and the scroll viewport — is what makes the filter bar
        immovable: a sticky bar inside the scroller still travels on the axis it
        is not pinned on, which is what Joel was seeing. It carries the page's
        `--page-px` because it sits OUTSIDE the padded content column, and
        `.flow-toolbar` turns that back into padding so the controls keep the
        page gutter while the bar's background spans the full width. */}
      {isFlow && (
        <div
          ref={setToolbarSlot}
          className={cn(
            'flow-toolbar-slot flex-shrink-0',
            edgeToEdge
              ? '[--page-px:0px]'
              : shouldNoPadding
                ? PAGE_PX_VARS_NO_PADDING
                : PAGE_PX_VARS,
          )}
        />
      )}

      {/* Viewport wrapper. In flow mode it is the positioning parent shared by
        the scroller and the overlay layer below it; in fill mode it is
        `display: contents`, so it disappears from layout entirely and the
        scroll div stays a direct flex child of the page column. */}
      <div className={isFlow ? FLOW_VIEWPORT : 'contents'}>
        {/* Scrollable content area - full width for scrolling anywhere.
        In flow mode this is THE page scroller and owns both axes: a wide table
        below scrolls the whole column sideways rather than a nested viewport,
        which is what keeps its sticky thead sticking. The literal
        `overflow-y-auto` class is load-bearing — ScrollRestoration selects it. */}
        <div
          ref={scrollContainerRef}
          className={cn(
            'min-h-0 flex-1',
            effectiveEnableScroll
              ? isFlow
                ? // `flow-scroller` isolates the sticky thead's stacking
                  // context (so the overlay layer paints above it) and adds
                  // the scroll padding that keeps the last rows clear of the
                  // pagination dock.
                  'flow-scroller overflow-x-auto overflow-y-auto overscroll-x-contain'
                : 'overflow-y-auto overflow-x-hidden'
              : '',
          )}
        >
          {/* Content container with max-width. In flow mode `flow-content` sizes
          it to max-content (min-width 100%): the scrolling document has to be
          as wide as its widest table, or a `position: sticky; left: 0` filter
          bar inside it has no slack and slides away with the rows. Its
          width/min-width beat the Tailwind utilities below by source order
          (table-styles.css is imported after index.css). */}
          <div
            className={cn(
              'relative mx-auto w-full min-w-0',
              // A width transition on a max-content column animates every data
              // load and drags the sticky chrome with it.
              isFlow ? 'flow-content' : 'transition-all duration-300',
              containerClasses,
              // Flow mode starts flush with the scrollport: the thead docks at
              // the scroller's own top edge, which is where the overlay layer
              // puts the column-customize button. A top margin here would
              // leave the two a few pixels out of line until the first scroll.
              // A full-height column fills the scroller exactly (h-full), so
              // a top margin would push it past the frame's bottom edge and
              // leave the whole page scrolling by a few pixels. Under tabs it
              // takes the same 4px as padding instead (border-box keeps the
              // height exact), so a space's full-height and scrolling views
              // don't jump 4px when you switch between them. A record page
              // takes its one tab gap in every mode instead (see isRecord).
              isRecord
                ? cn('mt-0', RECORD_TAB_GAP)
                : isFlow || edgeToEdge
                  ? 'mt-0'
                  : shouldUseFullHeight
                    ? hasTabs
                      ? 'mt-0 pt-1'
                      : 'mt-0'
                    : hasTabs
                      ? 'mt-1'
                      : effectiveNoHeaderSpacing
                        ? 'mt-0'
                        : header?.fixed
                          ? 'mt-2'
                          : 'mt-3',
              isFlow
                ? 'pb-24'
                : shouldUseFullHeight
                  ? 'flex h-full min-h-0 flex-col'
                  : 'pb-24',
            )}
          >
            {isLoading ? (
              <div className="scrollbar-thin flex-1">
                <div className="space-y-6 px-6 py-6">
                  {/* Properties Grid Skeleton - matches OverviewSkeleton */}
                  <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                    {[1, 2, 3, 4].map(i => (
                      <div
                        key={i}
                        className="rounded-inset flex animate-pulse flex-col gap-1.5 bg-muted px-3 py-2.5"
                      >
                        <Skeleton className="h-3 w-16 bg-muted-foreground/20" />
                        <Skeleton className="h-5 w-24 bg-muted-foreground/20" />
                      </div>
                    ))}
                  </div>

                  {/* Content Cards Skeleton */}
                  <div className="space-y-2">
                    {[1, 2].map(i => (
                      <div
                        key={i}
                        className="rounded-inset animate-pulse bg-muted p-3"
                      >
                        <div className="space-y-2">
                          <Skeleton className="h-3 w-3/4 bg-muted-foreground/20" />
                          <Skeleton className="h-3 w-1/2 bg-muted-foreground/20" />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <Page
                className={cn(
                  'transition-all duration-300',
                  className,
                  shouldUseFullHeight &&
                    !isFlow &&
                    'flex h-full min-h-0 flex-1 flex-col',
                )}
                noPadding={!shouldUsePadding}
                fullHeight={shouldUseFullHeight && !isFlow}
              >
                <RecordTabGapContext.Provider value={isRecord}>
                  {isFlow ? (
                    <TableScrollRootContext.Provider value={tableScrollRoot}>
                      {children}
                    </TableScrollRootContext.Provider>
                  ) : (
                    children
                  )}
                </RecordTabGapContext.Provider>
              </Page>
            )}
          </div>
        </div>

        {/* Overlay layer: a click-through sheet the size of the scrollport,
        painted above it. Chrome that must stay glued to the viewport rather
        than to the (max-content wide) document — the pagination dock, the
        column-customize button — is portalled in here by BaseTable. */}
        {isFlow && <div ref={setOverlaySlot} className="flow-overlay" />}
      </div>
    </div>
  )
}
